import { Fragment, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { connect } from 'react-redux'
import { bindActionCreators } from 'redux'
import ViewDocumentFileModal from './ViewDocumentFileModal'
import EditDocumentFileModal from './EditDocumentFileModal'
import FiltersModal, { FilterType } from '../../common/FiltersModal'
import SearchWithFilter from '../../common/SearchWithFilter'
import EmptyPageResults from '../../common/EmptyPageResults'
import LoadingSpinner from '../../common/LoadingSpinner'
import Dropdown from '../../common/Dropdown'
import SimpleButton from '../../common/SimpleButton'
import GenericTable from '../../common/GenericTable'
import ActionsMenu from '../../common/ActionsMenu'
import { toast } from '../../common/Toast'
import ConfirmationModal from '../../common/ConfirmationModal'
import { MenuKebabVerticalIcon, MenuKebabVerticalBackgroundIcon, EnabledIcon, DisabledIcon } from '../../../icons'
import { firmwareUploadDeletePermission, firmwareUploadWritePermission } from '../../../utils/permissionValidation'
import { loadFirmwareUploadDocuments, removeFirmwareDocument, clearFirmwareMessages } from '../../../actions/firmware'
import { useWindowSize } from '../../../libs/hooks'
import { useDeepCompareEffect } from '../../../hooks/useDeepCompareEffect'
import { groupModelsByDevice } from '../../../utils/functions'
import { languagesShort } from '../../../utils/languages'
import '../uploads.scss'

const orderingFields = [
  {
    field: 'title',
    order: 'asc',
    translationKey: 'title_asc',
  },
  {
    field: 'title',
    order: 'desc',
    translationKey: 'title_desc',
  },
  {
    field: 'isActive',
    order: 'asc',
    translationKey: 'status_asc',
  },
  {
    field: 'isActive',
    order: 'desc',
    translationKey: 'status_desc',
  },
]

const Documents = (props) => {
  const { t } = useTranslation()
  const isMobile = useWindowSize()[0] <= 768
  const [filesTableData, setFilesTableData] = useState([])
  const [selectedFilterModel, setSelectedFilterModel] = useState(null)
  const [selectedFilterType, setSelectedFilterType] = useState(null)
  const [statusesFilter, setStatusesFilter] = useState([])
  const [searchParam, setSearchParam] = useState(null)
  const [orderBy, setOrderBy] = useState(null)
  const [showSpinner, setShowSpinner] = useState(null)
  const [shouldApplyFilters, setShouldApplyFilters] = useState(true)
  const [showFiltersModal, setShowFiltersModal] = useState(false)
  const [showActionsMenu, setShowActionsMenu] = useState(null)
  const [canLoadMore, setCanLoadMore] = useState(false)
  const [selectedPage, setSelectedPage] = useState(1)
  const [isFirstLoad, setIsFirstLoad] = useState(true)
  const [expandedFile, setExpandedFile] = useState(0)
  const [showViewFileModal, setShowViewFileModal] = useState(false)
  const [showEditFileModal, setShowEditFileModal] = useState(false)
  const [selectedFile, setSelectedFile] = useState(null)
  const [showDeleteFileConfirmModal, setShowDeleteFileConfirmModal] = useState(false)
  const [fileIdDelete, setFileIdDelete] = useState(null)
  const statusOptions = ['active', 'inactive']

  useEffect(() => {
    const tableData = props.files.map((file, index) => {
      return {
        index,
        identifier: file.id,
        title: file.fileTitle,
        language: file.languageCode,
        models: groupModelsByDevice(file.models || []),
        status: file.isActive ? 'active' : 'inactive',
        actions: [
          {
            label: 'view_details',
            onActionClick: (e) => {
              e.stopPropagation()
              handleOpenViewEditFile(file.id)
            },
          },
          {
            label: 'edit',
            onActionClick: (e) => {
              e.stopPropagation()
              handleOpenViewEditFile(file.id, true)
            },
            requiredPermission: firmwareUploadWritePermission,
          },
          {
            label: 'delete',
            onActionClick: (e) => {
              e.stopPropagation()
              showConfirmationModal(file.id)
            },
            requiredPermission: firmwareUploadDeletePermission,
            isDisabled: file.isActive,
          },
        ],
      }
    })

    setFilesTableData(tableData)
  }, [props.files])

  useEffect(() => {
    setCanLoadMore(props.canLoadMore)
  }, [props.canLoadMore])

  useEffect(() => {
    setShowSpinner(props.isLoading)
  }, [props.isLoading])

  useEffect(() => {
    if (props.errorMessage) {
      toast.error(props.errorMessage)

      props.actions.clearFirmwareMessages()
    }
  }, [props.errorMessage])

  useEffect(() => {
    if (props.successMessage) {
      toast.success(props.successMessage)

      props.actions.clearFirmwareMessages()
    }
  }, [props.successMessage])

  useEffect(() => {
    if (selectedPage && selectedPage > 1) {
      handleLoadFiles(false)
    }
  }, [selectedPage])

  useDeepCompareEffect(() => {
    let changeValueTimeout = window.setTimeout(
      () => {
        if (shouldApplyFilters) {
          setSelectedPage(1)
          handleLoadFiles(true)
        }

        setShouldApplyFilters(true)
      },
      searchParam ? 1000 : 0,
    )
    return () => {
      clearTimeout(changeValueTimeout)
    }
  }, [searchParam, selectedFilterModel, selectedFilterType, statusesFilter, orderBy])

  const handleLoadFiles = (withReset) => {
    let queryParams = createQueryParams()

    queryParams = {
      ...queryParams,
      startPage: withReset ? 1 : selectedPage,
    }

    props.actions.loadFirmwareUploadDocuments(queryParams, withReset).then(() => setIsFirstLoad(false))
  }

  const handleSearch = (value) => {
    if (value?.length > 2) {
      setShowSpinner(true)
      setSearchParam(value)
    } else if (value?.length === 0 && searchParam?.length > 0) {
      setShowSpinner(true)
      setSearchParam(value)
    }
  }

  const handleSort = (orderingField) => {
    setOrderBy(orderingFields.find((o) => o.field === orderingField.field && o.order === orderingField.order))
  }

  const handleLoadMore = () => {
    setSelectedPage((prevValue) => (prevValue ? prevValue + 1 : 2))
  }

  const handleResetFilters = () => {
    setSelectedFilterModel(null)
    setSelectedFilterType(null)
    setStatusesFilter([])
  }

  const createQueryParams = () => {
    let queryParams = {}

    if (searchParam) {
      queryParams = { ...queryParams, query: searchParam }
    }

    if (selectedFilterModel) {
      queryParams = { ...queryParams, model: selectedFilterModel }
    }

    if (selectedFilterType) {
      queryParams = { ...queryParams, firmwareType: selectedFilterType }
    }

    if (statusesFilter.length === 1) {
      queryParams = { ...queryParams, isActive: statusesFilter[0] === statusOptions[0] }
    }

    if (orderBy?.field && orderBy?.order) {
      queryParams = { ...queryParams, orderBy: orderBy.field, order: orderBy.order }
    }

    return queryParams
  }

  const selectOrderByOptions = (orderByTranslationKey) => {
    const sortOption = orderingFields.find((field) => field.translationKey === orderByTranslationKey)
    handleSort(sortOption)
  }

  const handleOpenViewEditFile = (fileId, isEditMode) => {
    const file = props.files.find((f) => f.id === fileId)

    if (file) {
      isEditMode ? setShowEditFileModal(true) : setShowViewFileModal(true)
      setSelectedFile(file)
    }
  }

  const showConfirmationModal = (fileId) => {
    setFileIdDelete(fileId)
    setShowDeleteFileConfirmModal(true)
  }

  const handleDeleteFile = () => {
    setShowDeleteFileConfirmModal(false)
    props.actions.removeFirmwareDocument(fileIdDelete)
  }

  const closeModal = () => {
    setShowEditFileModal(false)
    setShowViewFileModal(false)
    setSelectedFile(null)
  }

  const toggleShowActionsMenu = (index) => {
    setShowActionsMenu(showActionsMenu === index ? null : index)
  }

  const renderFileStatus = (status) => {
    switch (status) {
      case statusOptions[0]:
        return (
          <div className="status-active">
            <EnabledIcon />
            <p>{t('active')}</p>
          </div>
        )
      case statusOptions[1]:
        return (
          <div className="status-inactive">
            <DisabledIcon />
            <p>{t('inactive')}</p>
          </div>
        )
      default:
        return null
    }
  }

  return (
    <Fragment>
      <div className="uploads-wrapper">
        <div className="actions-wrapper">
          <SearchWithFilter
            onSearch={(e) => handleSearch(e)}
            showFiltersModal={() => setShowFiltersModal(true)}
            onResetFilters={() => handleResetFilters()}
            showCounterBadge={selectedFilterModel || selectedFilterType || statusesFilter.length > 0}
            counterBadge={(selectedFilterModel && 1) + (selectedFilterType && 1) + statusesFilter.length}
          />
        </div>
        {isMobile && filesTableData.length > 0 && (
          <div className="order-by-mobile-wrapper">
            <Dropdown
              className="dropdown-input"
              placeholder={t('select_ordering')}
              options={orderingFields.map((field) => ({ key: field.translationKey, value: t(`sortable_context.${field.translationKey}`) }))}
              initialSelectedKey={orderBy?.translationKey}
              onChange={(orderBy) => selectOrderByOptions(orderBy.key)}
            />
          </div>
        )}
        {!isMobile && filesTableData.length > 0 && (
          <GenericTable
            data={filesTableData}
            headers={[
              {
                title: t('title'),
                orderKey: 'title',
              },
              {
                title: t('language'),
              },
              {
                title: t('monitoring.models'),
              },
              {
                title: t('status'),
                orderKey: 'isActive',
              },
              {
                title: '',
              },
            ]}
            keys={['title', 'language', 'models', 'status', 'action']}
            keyRenderer={{
              language: (item) => t(languagesShort[item.language?.toLowerCase()] || item.language),
              status: (item) => {
                return <div className="status-wrapper">{renderFileStatus(item.status)}</div>
              },
              action: (item) => {
                return (
                  <div
                    className="table-actions-wrapper"
                    onClick={(e) => {
                      e.stopPropagation()
                      toggleShowActionsMenu(item['index'])
                    }}
                  >
                    {showActionsMenu === item['index'] ? <MenuKebabVerticalBackgroundIcon /> : <MenuKebabVerticalIcon />}
                    {showActionsMenu === item['index'] && (
                      <ActionsMenu setHideMenu={() => setShowActionsMenu(null)} actions={item.actions} isLoading={showSpinner} />
                    )}
                  </div>
                )
              },
            }}
            onRowClick={(file) => handleOpenViewEditFile(file.identifier)}
            activeSort={orderBy}
            onSort={(orderingField) => handleSort(orderingField)}
            isLoading={showSpinner}
          />
        )}
        {isMobile &&
          filesTableData.map((file, index) => (
            <div key={index} className="card" onClick={() => setExpandedFile(index)}>
              <div className={'card-item' + (expandedFile !== index ? ' align-center' : '')}>
                <div className="card-item-title">{t('title')}</div>
                <div className={'card-item-body' + (expandedFile !== index ? ' align-center' : '')}>
                  <div>{file.title}</div>
                  <div
                    className="card-actions"
                    onClick={(e) => {
                      e.stopPropagation()
                      setShowActionsMenu(index)
                    }}
                  >
                    {showActionsMenu === index ? <MenuKebabVerticalBackgroundIcon /> : <MenuKebabVerticalIcon />}
                    {showActionsMenu === index && <ActionsMenu setHideMenu={() => setShowActionsMenu(null)} actions={file.actions} isLoading={showSpinner} />}
                  </div>
                </div>
              </div>
              {expandedFile === index && (
                <>
                  <div className="card-item">
                    <div className="card-item-title">{t('language')}</div>
                    <div className="card-item-body">{t(languagesShort[file.language?.toLowerCase()] || file.language)}</div>
                  </div>
                  <div className="card-item">
                    <div className="card-item-title">{t('monitoring.models')}</div>
                    <div className="card-item-body">{file.models}</div>
                  </div>
                  <div className="card-item">
                    <div className="card-item-title">{t('status')}</div>
                    <div className="card-item-body card-item-body-status">{renderFileStatus(file.status)}</div>
                  </div>
                </>
              )}
            </div>
          ))}
        {!showSpinner && !isFirstLoad && filesTableData.length === 0 && (
          <EmptyPageResults title={t('no_files_found')} subtitle={t('cannot_find_matching_search')} />
        )}
        {canLoadMore && (
          <div className="buttons-wrapper">
            <SimpleButton className="load-more-button" onClick={() => handleLoadMore()}>
              {t('load_more')}
            </SimpleButton>
          </div>
        )}
      </div>
      {showFiltersModal && (
        <FiltersModal
          filters={[
            {
              name: t('model'),
              type: FilterType.singleSelect,
              dataset: Array.from(new Set(props.prefixes.map((p) => p.models).flat())),
              input: selectedFilterModel,
              output: (filterModel) => {
                setSelectedFilterModel(filterModel)
              },
            },
            {
              name: t('status'),
              type: FilterType.multiSelect,
              dataset: statusOptions,
              input: statusesFilter,
              output: (filterStatuses) => {
                setStatusesFilter(filterStatuses)
              },
            },
          ]}
          shouldFilter={(shouldApply) => setShouldApplyFilters(shouldApply)}
          resetFilters={() => handleResetFilters()}
          closeFilters={() => setShowFiltersModal(false)}
        />
      )}
      {showSpinner && (
        <div className={'spinner-wrapper'}>
          <LoadingSpinner />
        </div>
      )}
      {showEditFileModal && <EditDocumentFileModal selectedFile={selectedFile} closeModal={closeModal} showSpinner={showSpinner} />}
      {showViewFileModal && (
        <ViewDocumentFileModal
          selectedFile={selectedFile}
          handleEditFile={() => {
            setShowViewFileModal(false)
            setShowEditFileModal(true)
          }}
          closeModal={closeModal}
        />
      )}
      {showDeleteFileConfirmModal && (
        <ConfirmationModal
          onToggle={() => setShowDeleteFileConfirmModal(false)}
          onAccept={() => handleDeleteFile()}
          onCancel={() => setShowDeleteFileConfirmModal(false)}
          message={t('confirm_delete_file')}
          acceptButtonText={t('delete')}
          isWarning
          requiredPermission={firmwareUploadDeletePermission}
        />
      )}
    </Fragment>
  )
}

function stateToProps({ firmware }) {
  return {
    files: firmware?.firmwareUploadDocuments || [],
    prefixes: firmware?.prefixes || [],
    isLoading: firmware?.isLoading,
    canLoadMore: firmware?.canLoadMoreFirmwareUploadDocuments,
    successMessage: firmware?.successMessage,
    errorMessage: firmware?.errorMessage,
  }
}

function dispatchToProps(dispatch) {
  return {
    actions: bindActionCreators(
      {
        loadFirmwareUploadDocuments,
        removeFirmwareDocument,
        clearFirmwareMessages,
      },
      dispatch,
    ),
  }
}

export default connect(stateToProps, dispatchToProps)(Documents)
